import { jsx } from "react/jsx-runtime";
import { Accordion } from "@zillow/constellation-10";
import React from "react";
const FAQItemIndexContext = React.createContext(void 0);
const FAQItem = React.forwardRef(({
  as: Component,
  value,
  children,
  ...props
}, ref) => {
  const autoIndex = React.useContext(FAQItemIndexContext);
  const itemValue = value ?? String(autoIndex ?? "");
  const itemCss = {
    borderWidth: "0",
    borderBottomWidth: "1px",
    borderStyle: "solid",
    borderColor: "border.muted",
    borderRadius: "0",
    marginBottom: "0"
  };
  if (Component) {
    return /* @__PURE__ */ jsx(Accordion.Item, { ref, asChild: true, value: itemValue, css: itemCss, ...props, children: /* @__PURE__ */ jsx(Component, { children }) });
  }
  return /* @__PURE__ */ jsx(Accordion.Item, { ref, value: itemValue, css: itemCss, ...props, children });
});
FAQItem.displayName = "FAQ.Item";
const FAQHeader = React.forwardRef(({
  as: Component,
  children,
  ...props
}, ref) => {
  const headerCss = {
    borderWidth: "0",
    backgroundColor: "transparent",
    flexDirection: "row-reverse",
    paddingInline: "0",
    // v8 had `${StyledText} { color: inherit }` so FAQ headings inherit
    // color from their parent container (e.g. white text on dark backgrounds).
    '& [data-c11n-component="Heading"], & [data-c11n-component="Text"]': {
      color: "inherit"
    },
    _hover: {
      backgroundColor: "transparent",
      '& [data-c11n-component="Heading"], & [data-c11n-component="Text"]': {
        textDecoration: "none",
        color: "inherit"
      }
    },
    _active: {
      backgroundColor: "transparent",
      '& [data-c11n-component="Heading"], & [data-c11n-component="Text"]': {
        textDecoration: "none",
        color: "inherit"
      }
    },
    '& [data-c11n-component="Icon"]': {
      color: "text.accent.brand.hero"
    }
  };
  if (Component) {
    return /* @__PURE__ */ jsx(Accordion.Header, { ref, asChild: true, css: headerCss, ...props, children: /* @__PURE__ */ jsx(Component, { children }) });
  }
  return /* @__PURE__ */ jsx(Accordion.Header, { ref, css: headerCss, ...props, children });
});
FAQHeader.displayName = "FAQ.Header";
const FAQPanel = React.forwardRef(({
  as: Component,
  children,
  ...props
}, ref) => {
  const panelCss = {
    backgroundColor: "transparent",
    borderTopWidth: "0",
    paddingInline: "0"
  };
  if (Component) {
    return /* @__PURE__ */ jsx(Accordion.Panel, { ref, asChild: true, css: panelCss, ...props, children: /* @__PURE__ */ jsx(Component, { children }) });
  }
  return /* @__PURE__ */ jsx(Accordion.Panel, { ref, css: panelCss, ...props, children });
});
FAQPanel.displayName = "FAQ.Panel";
const toStringValues = (val) => {
  if (val === void 0 || val === null) return void 0;
  if (Array.isArray(val)) return val.map((i) => String(i));
  return String(val);
};
const FAQ = React.forwardRef(({
  expanded,
  defaultExpanded,
  onChange,
  // Legacy styled-components API: consumers passed forwardedAs to override the root element
  forwardedAs,
  children,
  ...props
}, ref) => {
  const v10Expanded = React.useMemo(() => toStringValues(expanded), [expanded]);
  const v10DefaultExpanded = React.useMemo(() => toStringValues(defaultExpanded), [defaultExpanded]);
  const handleExpandedChange = React.useCallback((newValue) => {
    if (!onChange) return;
    const newValues = Array.isArray(newValue) ? newValue : newValue != null ? [newValue] : [];
    const newIndices = newValues.map((v) => Number(v));
    const oldIndices = Array.isArray(expanded) ? expanded : [];
    const opened = newIndices.find((i) => !oldIndices.includes(i));
    const closed = oldIndices.find((i) => !newIndices.includes(i));
    const toggledIndex = opened !== void 0 ? opened : closed;
    onChange(toggledIndex, newIndices);
  }, [onChange, expanded]);
  const wrappedChildren = React.Children.map(children, (child, index) => /* @__PURE__ */ jsx(FAQItemIndexContext.Provider, { value: index, children: child }));
  const rootProps = {
    ref,
    ...v10Expanded !== void 0 && {
      expanded: v10Expanded
    },
    ...v10DefaultExpanded !== void 0 && {
      defaultExpanded: v10DefaultExpanded
    },
    onExpandedChange: onChange ? handleExpandedChange : void 0,
    css: {
      gap: "0"
    },
    ...props
  };
  if (forwardedAs) {
    const RootElement = forwardedAs;
    return /* @__PURE__ */ jsx(Accordion.Root, { asChild: true, ...rootProps, children: /* @__PURE__ */ jsx(RootElement, { children: wrappedChildren }) });
  }
  return /* @__PURE__ */ jsx(Accordion.Root, { ...rootProps, children: wrappedChildren });
});
FAQ.displayName = "FAQ";
FAQ.Item = FAQItem;
FAQ.Header = FAQHeader;
FAQ.Panel = FAQPanel;
export {
  FAQ as default
};
